import { useQuery } from '@apollo/client/react';
import { FolderOpen, Pencil, Trash2 } from 'lucide-react';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import client from '@/apolloClient';
import useAppAbilities from '@/components/abilities';
import Empty from '@/components/blocks/Empty';
import EmptyContainer from '@/components/common/EmptyContainer';
import IconButton from '@/components/common/IconButton';
import Loader from '@/components/common/Loader';
import Table from '@/components/common/Table';
import TableFooter from '@/components/common/TableFooter';
import { useCompany } from '@/contexts/UserSession';
import { PerformanceResultType, UserRole } from '@/gql/enums';
import { formatDecimalValue } from '@/utils/common';
import { confirm } from '@/utils/ConfirmModal';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';
import useDateFormats from '@/utils/useDateFormats';
import { DeletePerformanceResultMutation, FetchPerformanceResults } from '../shared/PerformanceResults.graphql';
import useListReducer, { pageSizeOptions } from '../shared/useListReducer';
import { type AssessmentRunOption, type PerformanceFilterState, type PerformanceTabRef } from '../shared/utils';
import EditPerformanceBmpModal from './Edit/EditPerformanceBmpModal';

export interface BmpPerformanceRowData {
    id: string;
    traineeName: string;
    assessmentDate?: Date;
    percentage?: number;
    assessmentRunId?: string;
    isTiedToAssessmentRun?: boolean;
    assessmentRunName?: string;
    lastActionBy?: string;
    lastUpdated?: Date;
}

interface BmpPerformanceTabProps {
    filterState: PerformanceFilterState;
    assessmentRunOptions: AssessmentRunOption[];
}

const BmpPerformanceTab = forwardRef<PerformanceTabRef, BmpPerformanceTabProps>(
    ({ filterState, assessmentRunOptions }, ref) => {
        const { t } = useTranslation('performanceResult');
        const { formatDate, formatDateTimeWithSeconds } = useDateFormats();
        const { companyId: selectedCompanyId } = useCompany(true);
        const { getRoleLabel } = useCompanyRoleLabels();
        const abilities = useAppAbilities();

        const { filterAssessmentRunIds, traineeName, groupId, subGroupId } = filterState;

        const [state, dispatch] = useListReducer();
        const { page, pageSize } = state;

        const [openEditModal, setOpenEditModal] = useState(false);
        const [editData, setEditData] = useState<BmpPerformanceRowData | null>(null);

        const filter = useMemo(
            () => ({
                assessmentRunIds: filterAssessmentRunIds.length > 0 ? filterAssessmentRunIds : null,
                searchText: traineeName ?? undefined,
                resultTypes: [PerformanceResultType.Bmp],
                companyIds: selectedCompanyId ? [selectedCompanyId] : null,
                groupIds: groupId ? [groupId] : null,
                subgroupIds: subGroupId ? [subGroupId] : null,
            }),
            [filterAssessmentRunIds, traineeName, selectedCompanyId, groupId, subGroupId]
        );

        const { loading, data, error, refetch } = useQuery(FetchPerformanceResults, {
            fetchPolicy: 'cache-and-network',
            variables: {
                pagination: {
                    limit: pageSize,
                    offset: (page - 1) * pageSize,
                },
                filter,
            },
        });

        useImperativeHandle(
            ref,
            () => ({
                refetch,
            }),
            [refetch]
        );

        const totalCount = useMemo(() => data?.performanceResults?.count || 0, [data]);

        const handleEdit = useCallback((row: BmpPerformanceRowData) => {
            setOpenEditModal(true);
            setEditData(row);
        }, []);

        const handleDelete = useCallback(
            (record: BmpPerformanceRowData) => {
                confirm(
                    {
                        title: t('actions.deleteDetail.title'),
                        content: t('actions.deleteDetail.message'),
                        isDeletion: true,
                    },
                    async () => {
                        try {
                            const { data } = await client.mutate({
                                mutation: DeletePerformanceResultMutation,
                                variables: { id: record.id },
                            });

                            if (data?.deletePerformanceResult?.__typename === 'ErrorResponse') {
                                const errorResponse = data.deletePerformanceResult;
                                const specificErrorField = errorResponse.fields?.find(f => f.message);
                                const errorMessage =
                                    specificErrorField?.message || t('deleteError.description', { type: 'BMP' });

                                showErrorNotification({
                                    message: t('deleteError.title', { type: 'BMP' }),
                                    description: errorMessage,
                                });

                                throw new Error(errorMessage);
                            } else {
                                showSuccessNotification({
                                    message: t('deleteSuccess.title', { type: 'BMP' }),
                                    description: t('deleteSuccess.message', { type: 'BMP' }),
                                });

                                void refetch();
                            }
                        } catch (error) {
                            console.error('Error deleting BMP performance result:', error);
                            throw error;
                        }
                    }
                );
                return true;
            },
            [t, refetch]
        );

        const handleOnEditModalClose = useCallback(
            (refresh?: boolean) => {
                setOpenEditModal(false);
                setEditData(null);

                if (refresh) {
                    void refetch();
                }
            },
            [refetch]
        );

        const columns = useMemo(
            () => [
                {
                    title: t('bmp.table.traineeName', { role: getRoleLabel(UserRole.Trainee) }),
                    dataIndex: 'traineeName',
                    key: 'traineeName',
                    className: 'font-semibold',
                },
                {
                    title: t('bmp.table.assessmentDate'),
                    dataIndex: 'assessmentDate',
                    key: 'assessmentDate',
                    render: (value: Date | null | undefined) => {
                        if (!value) {
                            return t('emptyValue');
                        }
                        return formatDate(value);
                    },
                },
                {
                    title: t('bmp.table.marksmanship'),
                    dataIndex: 'percentage',
                    key: 'percentage',
                    render: (value: number | null | undefined) => {
                        if (value === null || value === undefined) {
                            return t('emptyValue');
                        }
                        return t('common:formats.percentageValue', { value: formatDecimalValue(value) });
                    },
                },
                {
                    title: t('filter.assessmentRun'),
                    dataIndex: 'assessmentRunName',
                    key: 'assessmentRunName',
                    render: (value: string | null | undefined) => value ?? t('emptyValue'),
                },
                {
                    title: t('bmp.table.lastUpdated'),
                    dataIndex: 'lastUpdated',
                    key: 'lastUpdated',
                    render: (value: Date | null | undefined) => {
                        if (!value) {
                            return t('emptyValue');
                        }
                        return formatDateTimeWithSeconds(value);
                    },
                },
                {
                    title: t('bmp.table.lastActionBy'),
                    dataIndex: 'lastActionBy',
                    key: 'lastActionBy',
                },
                {
                    title: t('bmp.table.actions'),
                    dataIndex: 'actions',
                    key: 'actions',
                    className: 'text-right',
                    render: (_: any, record: BmpPerformanceRowData) =>
                        abilities.canManagePerformanceResultsForCompany(selectedCompanyId) && (
                            <div className="flex justify-end gap-5">
                                <IconButton
                                    onClick={() => handleEdit(record)}
                                    title={t('actions.editBtn')}
                                    icon={<Pencil className="h-4 w-4 text-yellow-500" strokeWidth={2} />}
                                />
                                <IconButton
                                    onClick={() => handleDelete(record)}
                                    title={t('actions.deleteBtn')}
                                    icon={<Trash2 className="h-4 w-4 text-red-500" strokeWidth={2} />}
                                />
                            </div>
                        ),
                },
            ],
            [
                t,
                getRoleLabel,
                formatDate,
                formatDateTimeWithSeconds,
                abilities,
                selectedCompanyId,
                handleEdit,
                handleDelete,
            ]
        );

        const dataSource: BmpPerformanceRowData[] = useMemo(() => {
            if (data?.performanceResults?.items) {
                return data.performanceResults.items
                    .map((item: any) => {
                        if (item.result.__typename !== 'BmpResult') {
                            return null;
                        }

                        return {
                            id: item.id,
                            traineeName: item.user?.name || t('emptyValue'),
                            assessmentDate: item.completedAt ? new Date(item.completedAt) : undefined,
                            percentage: item.result.percentage ?? undefined,
                            assessmentRunId: item.assessmentRunId ?? undefined,
                            isTiedToAssessmentRun: item.isTiedToAssessmentRun ?? false,
                            assessmentRunName: item.assessmentRun?.name || t('emptyValue'),
                            lastActionBy: item.updatedByUser?.name || t('emptyValue'),
                            lastUpdated: item.updatedAt ? new Date(item.updatedAt) : undefined,
                        };
                    })
                    .filter(item => item !== null);
            }

            return [];
        }, [data, t]);

        const handlePageChange = useCallback(
            (newPage: number) => {
                dispatch({ type: 'SET_PAGE', page: newPage });
            },
            [dispatch]
        );

        const handlePageSizeChange = useCallback(
            (newPageSize: number) => {
                dispatch({ type: 'SET_PAGE_SIZE', pageSize: newPageSize });
            },
            [dispatch]
        );

        if (loading && !dataSource.length) {
            return <Loader />;
        }

        if ((!dataSource.length && !loading) || error) {
            return (
                <EmptyContainer>
                    <Empty
                        title={t('error.noRecordsFound')}
                        description={t('error.noRecordsFoundDesc')}
                        icon={FolderOpen}
                    />
                </EmptyContainer>
            );
        }

        return (
            <div>
                <Table<BmpPerformanceRowData> columns={columns} dataSource={dataSource} rowKey="id" />

                {totalCount > 0 && (
                    <div className="mt-4">
                        <TableFooter
                            currentPage={page}
                            pageSize={pageSize}
                            totalCount={totalCount}
                            pageSizeOptions={pageSizeOptions}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </div>
                )}

                <EditPerformanceBmpModal
                    key={openEditModal ? 'open' : 'close'}
                    open={openEditModal}
                    onClose={handleOnEditModalClose}
                    rowData={editData}
                    assessmentRunOptions={assessmentRunOptions}
                />
            </div>
        );
    }
);

BmpPerformanceTab.displayName = 'BmpPerformanceTab';

export default BmpPerformanceTab;
